import { PackageCheck } from 'lucide-react';
import type { Station } from '@hcc/types';
import { ConfirmItemRow } from './ConfirmItemRow';
import type { ConfirmRow } from './useKitConfirmItems';

/**
 * "Items to confirm" section for TUPE transferees: assets the person
 * already holds, which the desk verifies (correcting identifiers, with
 * optional service date and condition photo) or marks not present, rather
 * than issuing.
 */
export function ConfirmPanel({
  rows,
  station,
  onOutcome,
  onServiceDate,
  onPhoto,
  onSerial,
  onRegistration,
  onAttributes,
}: {
  rows: ConfirmRow[];
  station: Station;
  onOutcome: (assetId: string, outcome: 'confirmed' | 'not_present') => void;
  onServiceDate: (assetId: string, date: string) => void;
  onPhoto: (assetId: string, dataUrl: string | undefined) => void;
  onSerial: (assetId: string, value: string) => void;
  onRegistration: (assetId: string, value: string) => void;
  onAttributes: (assetId: string, attrs: Record<string, string>) => void;
}) {
  const unresolved = rows.filter((r) => r.outcome === null).length;
  return (
    <section className="mt-6 rounded-token border border-info-soft/25 bg-info-soft/10 p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
          <PackageCheck className="h-4 w-4 text-info-soft-fg" />
          Items to confirm
        </h3>
        <span className="text-xs text-fg-muted">
          {unresolved > 0 ? `${unresolved} to check` : 'all checked'}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-fg-muted">
        Existing kit this person already holds. Confirm each item they have (record its last service
        date and a condition photo if useful), or mark it not present so it gets chased.
      </p>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <ConfirmItemRow
            key={row.asset.id}
            row={row}
            station={station}
            onOutcome={(outcome) => onOutcome(row.asset.id, outcome)}
            onServiceDate={(date) => onServiceDate(row.asset.id, date)}
            onPhoto={(dataUrl) => onPhoto(row.asset.id, dataUrl)}
            onSerial={(value) => onSerial(row.asset.id, value)}
            onRegistration={(value) => onRegistration(row.asset.id, value)}
            onAttributes={(attrs) => onAttributes(row.asset.id, attrs)}
          />
        ))}
      </div>
    </section>
  );
}
